import { useEffect, useState } from "react";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TroubleshootIcon from "@mui/icons-material/Troubleshoot";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { gridActionSx } from "../widgets/gridActionSx";
import ActionButton from "../widgets/ActionButton";
import {
  GET_DIAMS,
  GET_SAVED_QUERIES,
  UPDATE_DASHBOARD_QUERY,
  UPDATE_DASHBOARD_QUERY_COLUMNS,
  UPDATE_DASHBOARD_QUERY_NAME,
  UPDATE_DASHBOARD_QUERY_SECTION,
  UPDATE_DASHBOARD_QUERY_VISIBILITY,
  tokenOptions,
} from "../../backend/graphql";
import {
  GraphQLDataGrid,
  GraphQLGridField,
  GraphQLGridParameters,
  parseSavedColumns,
} from "../widgets/GraphQLDataGrid";
import SavedQueryDialog, { SavedQueryDraft } from "./SavedQueryDialog";
import { Box, Stack } from "@mui/material";
import PageTitle from "../widgets/PageTitle";
import {
  adminTagsColumn,
  gradingActions,
  handleNavigate,
  handleNavigatePrice,
  renderPricing,
} from "./Stock";
import { Roles, seesGrading } from "../../app/features/auth/authTypes";
import { useParams } from "react-router-dom";
import { useAppSelector } from "../../app/hooks";
import { useMutation, useQuery } from "@apollo/client";

// The columns a data-quality tile can show. Named here rather than inside the grid's
// parameters because the edit dialog offers the very same list.
// The columns of a saved query's grid, for the role looking at it.
const diamondFields = (role: Roles): GraphQLGridField[] => [
  { field: "representationImageID", label: "Image", type: "image" },
  { field: "id", label: "ID", visible: false, width: 300, type: "uuid" },
  { field: "source", label: "Source", flex: 0.5, visible: false },
  { field: "sKU", label: "SKU", width: 80, defaultSort: "asc" },
  { field: "carat", label: "Carat", width: 80, type: "number" },
  { field: "cut", label: "Cut", visible: false },
  { field: "shape", label: "Shape", visible: false },
  { field: "clarity", label: "Clarity", width: 80 },
  { field: "color", label: "Color", flex: 2 },
  { field: "langermanColor", label: "Langerman Color", flex: 2 },
  { field: "flat", label: "Flat", type: "number", visible: false },
  { field: "melee", label: "Melee", type: "number", visible: false },
  {
    field: "isGIACertified",
    label: "GIA?",
    flex: 0.5,
    type: "boolean",
    visible: false,
  },
  { field: "tags", label: "Tags", flex: 1 },
  ...adminTagsColumn(role),
  { field: "price", label: "Price", flex: 0.5, type: "number" },
  {
    field: "currentPricePercentile",
    label: "Pricing",
    flex: 0.5,
    type: "number",
    renderCell: (params: any) => renderPricing(params.value),
  },
  { field: "pricingRemark", label: "Remark", flex: 2, visible: false },
  {
    field: "lastFantasyUpdate",
    label: "Last Check",
    type: "dateTimeShort",
    visible: false,
  },
  {
    field: "ignorePricing",
    label: "Ignore Price",
    type: "boolean",
    width: 100,
    visible: false,
  },
  {
    field: "noLongerAvailable",
    label: "Unavailable",
    type: "boolean",
    width: 100,
    visible: false,
  },
  {
    field: "hasGoodImage",
    label: "Good image",
    type: "boolean",
    width: 100,
    visible: false,
  },
  {
    field: "hasWebImage",
    label: "Web image",
    type: "boolean",
    width: 100,
    visible: false,
  },
  {
    field: "hasOriginalImage",
    label: "Camera image",
    type: "boolean",
    width: 100,
    visible: false,
  },
  {
    field: "hasPatternImage",
    label: "Pattern image",
    type: "boolean",
    width: 100,
    visible: false,
  },
  {
    field: "hasCalibratedImage",
    label: "Calibrated image",
    type: "boolean",
    width: 100,
    visible: false,
  },
  {
    field: "hasImageProcessingError",
    label: "Image Processing Error",
    type: "boolean",
    width: 100,
    visible: false,
  },
  {
    field: "hasApproximateCalibration",
    label: "Approximate Calibration",
    type: "boolean",
    width: 100,
    visible: false,
  },
];

const gridParameters: (
  query: string,
  role: Roles,
  isDeveloper: boolean,
  savedColumns?: string,
) => GraphQLGridParameters = (
  query: string,
  role: Roles,
  isDeveloper: boolean,
  savedColumns?: string,
) => {
  return {
    graphqlQuery: GET_DIAMS,
    graphqlFilter: query,
    savedColumns: savedColumns,
    graphqlItemsPath: (data) => data.diamonds,
    rowHeight: 64,
    allRows: true,
    fields: diamondFields(role),
    // Analyse, pricing and — for whoever sees it — grading.
    maxActions: seesGrading(role, isDeveloper) ? 3 : 2,
    getActions: ({ id, navigate, row }) => {
      // We create a list of GridActionsCellItem where we add items depending on conditions

      let actions = [];

      if (row.mainImageID || row.source === "Langerman") {
        actions.push(
          <GridActionsCellItem
            icon={<TroubleshootIcon />}
            label="Analyse"
            sx={gridActionSx}
            onClick={() => handleNavigate(row, navigate)}
          />,
        );
      }
      if (
        row.price &&
        row.clarity &&
        row.clarity !== "Unknown" &&
        row.color &&
        row.color !== "Unknown" &&
        (!row.tags || !row.tags.includes("BOX"))
      ) {
        actions.push(
          <GridActionsCellItem
            icon={<AttachMoneyIcon />}
            label="Pricing"
            sx={gridActionSx}
            onClick={() => handleNavigatePrice(row, navigate)}
          />,
        );
      }
      actions.push(...gradingActions(row, seesGrading(role, isDeveloper)));

      return actions;
    },
  };
};

const DataQualityTopic = () => {
  const { topic } = useParams();
  const token = useAppSelector((state) => state.auth.token);
  const role = useAppSelector((state) => state.auth.role);
  const isDeveloper = useAppSelector((state) => state.auth.isDeveloper);
  const { loading, data, refetch } = useQuery(GET_SAVED_QUERIES, {
    ...tokenOptions(token),
    variables: {
      take: 100,
      skip: 0,
      filter: 'iD == "' + topic + '"',
    },
  });

  const savedQuery = data?.savedQueries?.items?.[0];

  // Switching role (RoleSwitcher) mints a new token but doesn't change this query's variables, so
  // Apollo won't refetch on its own — the Admin-only filtering only takes effect server-side once
  // this is reissued with the new token.
  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  const [updateVisibility] = useMutation(
    UPDATE_DASHBOARD_QUERY_VISIBILITY,
    tokenOptions(token),
  );

  // Admins can adjust this tile's columns without re-saving the whole query.
  const buildParameters = (sq: any) => {
    const params = gridParameters(sq.query, role, isDeveloper, sq.columns);
    if (role === "Admin") {
      params.graphqlUpdateColumns = {
        id: sq.id,
        mutation: UPDATE_DASHBOARD_QUERY_COLUMNS,
        refetch,
      };
    }
    return params;
  };

  // Editing happens in a dialog of its own, mounted only while it is open: what is typed there
  // never reaches this component, so the grid below is not re-rendered on every keystroke, and the
  // saved query is left alone until Save.
  const [isEditing, setIsEditing] = useState(false);
  const [updateQuery, { loading: saving }] = useMutation(
    UPDATE_DASHBOARD_QUERY,
    tokenOptions(token),
  );
  const [updateName] = useMutation(
    UPDATE_DASHBOARD_QUERY_NAME,
    tokenOptions(token),
  );
  const [updateSection] = useMutation(
    UPDATE_DASHBOARD_QUERY_SECTION,
    tokenOptions(token),
  );
  const [updateColumns] = useMutation(
    UPDATE_DASHBOARD_QUERY_COLUMNS,
    tokenOptions(token),
  );

  // What the dialog opens on. Columns fall back to the fields' own defaults, exactly as the grid
  // does when a saved query has none stored.
  const draftOf = (sq: any): SavedQueryDraft => ({
    name: sq.name ?? "",
    section: sq.section ?? "",
    query: sq.query ?? "",
    adminOnly: !!sq.adminOnly,
    columns:
      parseSavedColumns(sq.columns, diamondFields(role)) ??
      diamondFields(role)
        .filter((field) => field.visible !== false)
        .map((field) => field.field),
  });

  // Only what actually changed is sent — each part of a saved query has its own mutation.
  const handleSave = async (draft: SavedQueryDraft) => {
    if (!savedQuery) return;
    const initial = draftOf(savedQuery);
    if (draft.name !== initial.name) {
      await updateName({ variables: { id: savedQuery.id, name: draft.name } });
    }
    if (draft.section !== initial.section) {
      await updateSection({
        variables: { id: savedQuery.id, section: draft.section },
      });
    }
    if (draft.query !== initial.query) {
      await updateQuery({
        variables: { id: savedQuery.id, query: draft.query },
      });
    }
    if (draft.adminOnly !== initial.adminOnly) {
      await updateVisibility({
        variables: { id: savedQuery.id, adminOnly: draft.adminOnly },
      });
    }
    if (draft.columns.join() !== initial.columns.join()) {
      await updateColumns({
        variables: {
          id: savedQuery.id,
          columns: JSON.stringify(draft.columns),
        },
      });
    }
    setIsEditing(false);
    refetch();
  };

  return (
    <Stack spacing={1} useFlexGap>
      <Box>
        <PageTitle
          title={savedQuery?.name}
          actions={
            role === "Admin" &&
            savedQuery && (
              <ActionButton size="small" onClick={() => setIsEditing(true)}>
                Edit Query
              </ActionButton>
            )
          }
        />
        {isEditing && savedQuery && (
          <SavedQueryDialog
            title="Edit query"
            initial={draftOf(savedQuery)}
            fields={diamondFields(role)}
            saving={saving}
            onClose={() => setIsEditing(false)}
            onSave={handleSave}
          />
        )}
      </Box>
      {!topic ? (
        <p>No topic found!</p>
      ) : loading ? (
        <p>Loading...</p>
      ) : !savedQuery ? (
        <p>No topic found!</p>
      ) : (
        <GraphQLDataGrid parameters={buildParameters(savedQuery)} elevated />
      )}
    </Stack>
  );
};

export default DataQualityTopic;
